import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import { formatWalletType } from "../../../utils/walletTypes";
import {
  asNum,
  formatDateTime,
  formatMoney,
  isUnauthorized,
  releasedByLabel,
  userDisplayName,
} from "./roundReleaseLedgerUtils";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Staking/StakingStakesPage.css";
import "./RoundReleaseLedgerPage.css";

const DEFAULT_TABS = [
  { round: 1, percentage: 10, user_count: 0, total_amount: 0, currency: "USDT" },
  { round: 2, percentage: 15, user_count: 0, total_amount: 0, currency: "USDT" },
  { round: 3, percentage: 25, user_count: 0, total_amount: 0, currency: "USDT" },
  { round: 4, percentage: 50, user_count: 0, total_amount: 0, currency: "USDT" },
];

const LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const API_MAX_LIMIT = 100;
const EXCEL_SHEET_TITLE = "Round report";

const parseRound = (value) => {
  const n = parseInt(String(value || ""), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

const rowToExport = (row) => {
  const user = row?.user || {};
  return {
    User: userDisplayName(user),
    Email: user.email || "",
    UUID: user.uuid || "",
    Phone: user.phone || "",
    Round: row.round ?? "",
    "Amount received": asNum(row.amount),
    Currency: row.currency || "",
    "% of total": row.percentage != null ? row.percentage : "",
    Wallet: formatWalletType(row.wallet_type || "staking"),
    "Released at": formatDateTime(row.released_at),
    "Released by": releasedByLabel(row.released_by),
  };
};

const RoundReleaseReportPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const round = parseRound(searchParams.get("round"));

  const [rows, setRows] = useState([]);
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 700);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [round, debouncedUser, pageLimit]);

  const fetchApiPage = useCallback(
    async (pageNum, limitNum) => {
      const params = {
        round,
        page: pageNum,
        limit: Math.min(limitNum, API_MAX_LIMIT),
      };
      if (debouncedUser) params.user = debouncedUser;
      return AdminService.getReferralReleaseRoundReports(params);
    },
    [round, debouncedUser]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const apiPageSize = Math.min(pageLimit, API_MAX_LIMIT);
      const chunksPerView = Math.max(1, Math.ceil(pageLimit / apiPageSize));
      const startApiPage = pageLimit > apiPageSize ? (page - 1) * chunksPerView + 1 : page;

      const first = await fetchApiPage(startApiPage, apiPageSize);
      if (isUnauthorized(first)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      if (first?.success === false) {
        setRows([]);
        setError(first?.message || "Failed to fetch round release report");
        if (Array.isArray(first?.tabs) && first.tabs.length) setTabs(first.tabs);
        setSummary(first?.summary || null);
        return;
      }

      const list = Array.isArray(first?.data) ? [...first.data] : [];
      const pag = first?.pagination || {};
      const total = typeof pag.total === "number" ? pag.total : list.length;

      if (chunksPerView > 1) {
        for (let i = 1; i < chunksPerView; i += 1) {
          const more = await fetchApiPage(startApiPage + i, apiPageSize);
          if (isUnauthorized(more)) {
            clearAuth();
            window.location.href = "/";
            return;
          }
          const batch = Array.isArray(more?.data) ? more.data : [];
          list.push(...batch);
          if (!more?.pagination?.has_next) break;
        }
      }

      setRows(list);
      setTabs(Array.isArray(first?.tabs) && first.tabs.length ? first.tabs : DEFAULT_TABS);
      setSummary(first?.summary || null);
      setPagination({
        page,
        limit: pageLimit,
        total,
        total_pages: Math.max(1, Math.ceil(total / pageLimit) || Number(pag.total_pages) || 1),
        has_next: page * pageLimit < total,
        has_prev: page > 1,
      });
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to fetch round release report");
    } finally {
      setLoading(false);
    }
  }, [fetchApiPage, page, pageLimit]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const fetchAllForExport = useCallback(async () => {
    const all = [];
    let currentPage = 1;
    let lastPage = 1;
    do {
      const res = await fetchApiPage(currentPage, API_MAX_LIMIT);
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return [];
      }
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  }, [fetchApiPage]);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllForExport();
      if (!allRows.length) {
        toast.error("No paid users in this round to export.");
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(allRows.map(rowToExport));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `round_${round}_release_report_${dateStamp}.xlsx`);
      toast.success(`Exported ${allRows.length} row(s) to Excel.`);
    } catch (err) {
      toast.error(err?.message || "Failed to export Excel file.");
    } finally {
      setExportLoading(false);
    }
  };

  const selectRound = (nextRound) => {
    const n = parseRound(nextRound);
    setSearchParams({ round: String(n) });
  };

  const activeTab = useMemo(() => {
    return tabs.find((t) => Number(t.round) === round) || summary || { round };
  }, [tabs, round, summary]);

  const summaryAmounts = useMemo(() => {
    if (Array.isArray(summary?.amounts) && summary.amounts.length) return summary.amounts;
    if (summary?.total_amount != null) {
      return [
        {
          currency: summary.currency || "USDT",
          user_count: summary.user_count,
          total_amount: summary.total_amount,
        },
      ];
    }
    return [];
  }, [summary]);

  const pageStart = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = rows.length === 0 ? 0 : pageStart + rows.length - 1;
  const emptyForRound = !loading && !error && rows.length === 0;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Referral</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Round Release Report</span>
          </p>
          <h1 className="dashboard-hero__title">Round Release Report</h1>
          <p className="dashboard-hero__subtitle">
            Users already credited for each round — one staking credit per user
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-list-alt" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="rrl-chips" role="tablist" aria-label="Release round">
          {tabs.map((tab) => {
            const n = Number(tab.round);
            const active = n === round;
            return (
              <button
                key={n}
                type="button"
                className={`rrl-chip ${active ? "rrl-chip--active" : ""}`}
                onClick={() => selectRound(n)}
              >
                Round {n}
                <span className="rrr-tab-count">{tab.user_count ?? 0}</span>
              </button>
            );
          })}
        </div>

        <p className="rrr-summary" role="status">
          Round {activeTab.round}
          {activeTab.percentage != null ? ` · ${activeTab.percentage}%` : ""}
          {summary?.user_count != null ? ` · ${summary.user_count} users` : ""}
          {summaryAmounts.length
            ? summaryAmounts.map((a) => ` · ${formatMoney(a.total_amount, a.currency)}`).join("")
            : ""}
          {activeTab.from_month != null && activeTab.to_month != null
            ? ` · months ${activeTab.from_month}–${activeTab.to_month}`
            : ""}
        </p>

        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-users" />
              </span>
              <h2 className="openorders-section__title">Paid in Round {round}</h2>
            </div>
            <div className="rrr-header-actions">
              <button
                type="button"
                className="rrr-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download this round's paid users as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--outline"
                onClick={fetchRows}
                disabled={loading || exportLoading}
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>

          <div className="rrl-filters">
            <input
              type="search"
              placeholder="Email, UUID, phone, name"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input rrl-filters__search"
            />
            <label className="staking-page-limit">
              <span>Rows</span>
              <select
                value={pageLimit}
                onChange={(e) => setPageLimit(Number(e.target.value))}
                className="openorders-search-input staking-page-limit__select"
              >
                {LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="rrl-empty rrl-empty--error">
              <p>{error}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={fetchRows}>
                Retry
              </button>
            </div>
          ) : emptyForRound ? (
            <div className="rrl-empty">
              <i className="fas fa-inbox" />
              <p>
                {debouncedUser
                  ? "No matching paid users in this round"
                  : "No users paid in this round yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table rrl-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>UUID</th>
                      <th>Amount received</th>
                      <th>% of total</th>
                      <th>Wallet</th>
                      <th>Released at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const user = row.user || {};
                      const ccy = row.currency || "USDT";
                      return (
                        <tr key={`${user.id || user.uuid || idx}-${row.round}-${ccy}-${idx}`}>
                          <td>
                            <div className="rrl-user">
                              <strong>{userDisplayName(user)}</strong>
                            </div>
                          </td>
                          <td>{user.email || "—"}</td>
                          <td>{user.uuid ? <code>{user.uuid}</code> : "—"}</td>
                          <td className="rrl-amt--paid">{formatMoney(row.amount, ccy)}</td>
                          <td>{row.percentage != null ? `${row.percentage}%` : "—"}</td>
                          <td>{formatWalletType(row.wallet_type || "staking")}</td>
                          <td>{formatDateTime(row.released_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="rrl-pagination">
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_prev || loading || page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="openorders-stat" style={{ margin: 0 }}>
                  Showing {pageStart}–{pageEnd}
                  {pagination.total != null ? ` of ${pagination.total}` : ""}
                </span>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_next || loading || page >= pagination.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default RoundReleaseReportPage;
